// MARK: used in v2
import { Cart, CartItem } from "@yoco/shop-sdk/lib/types"
import { navigate } from "gatsby"

import { CheckoutContext } from "./store"
import { useInitCart } from "../cart/hooks"
import { CartContext } from "../cart/store"
import { NotificationContext } from "../notifications/context"
import {
  shop,
  clearCart,
  fetchCartId,
  fetchCartProducts,
  fetchCartPromoCode,
  storeCartPromoCode,
  clearCartPromoCode,
  StoredBundle,
  StoredProduct,
  StoredItem,
  isStoredBundle,
  isStoredProduct,
  sessionStorageAvailable,
} from "../utils/shop"

import { CSRF_TOKEN_SESSION_KEY } from "@constants/V2/auth"
import { useGetBundleByCode } from "@hooks/bundles"
import { useGetProductBySku } from "@hooks/products"
import { trackSegmentEvent } from "@utils/analytics"
import { withPrefix, getSignupQueryString } from "@utils/url"
import { useCallback, useContext, useRef, useState } from "react"

const useSyncCartItems = () => {
  const getProduct = useGetProductBySku()
  const getBundle = useGetBundleByCode()
  const checkoutContext = useContext(CheckoutContext)
  const itemsToAdd = useRef<
    {
      itemID: number
      quantity: number
      isBundle: boolean
    }[]
  >([])

  const syncCartItems = useCallback(
    async (
      cartId: string,
      localCart: StoredItem[],
      initialRemoteCart: Cart
    ) => {
      for (const storedItem of localCart) {
        if (isStoredProduct(storedItem)) {
          await syncCartProduct(cartId, storedItem, initialRemoteCart)
        } else {
          await syncCartBundle(cartId, storedItem, initialRemoteCart)
        }
      }
      if (itemsToAdd.current.length > 0) {
        const response = await shop.carts.bulkAdd(cartId, itemsToAdd.current)
        checkoutContext.setRemoteCart(response)
        itemsToAdd.current = []
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const syncCartProduct = useCallback(
    async (cartId: string, product: StoredProduct, initialRemoteCart: Cart) => {
      const sdkProduct = getProduct(product.sku, product.storefront)

      if (!sdkProduct) {
        console.error(`Failed to get product with sku: ${product.sku}`)
        throw new Error()
      }

      const inRemoteCart = initialRemoteCart.items.find((item) => {
        return item.product.sku === product.sku && !item.bundle
      })

      if (!inRemoteCart) {
        itemsToAdd.current.push({
          itemID: Number.parseInt(sdkProduct.externalId),
          quantity: product.quantity,
          isBundle: false,
        })
      } else if (inRemoteCart.quantity !== product.quantity) {
        if (inRemoteCart.quantity > product.quantity) {
          const response = await shop.carts.removeProduct(
            cartId,
            inRemoteCart.product.id,
            inRemoteCart.quantity - product.quantity
          )
          checkoutContext.setRemoteCart(response)
        } else {
          itemsToAdd.current.push({
            itemID: Number.parseInt(sdkProduct.externalId),
            quantity: product.quantity - inRemoteCart.quantity,
            isBundle: false,
          })
        }
      }
    },
    [checkoutContext, getProduct]
  )

  const syncCartBundle = useCallback(
    async (cartId: string, bundle: StoredBundle, initialRemoteCart: Cart) => {
      const sdkBundle = getBundle(bundle.bundleCode)

      if (!sdkBundle) {
        console.error(`Failed to get bundle with code: ${bundle.bundleCode}`)
        throw new Error()
      }

      const inRemoteCart = initialRemoteCart.items.find((item) => {
        return item.bundle?.bundle_code === bundle.bundleCode
      })

      if (!inRemoteCart) {
        itemsToAdd.current.push({
          itemID: Number.parseInt(sdkBundle.externalId),
          quantity: bundle.quantity,
          isBundle: true,
        })
      } else if (inRemoteCart.quantity !== bundle.quantity) {
        if (inRemoteCart.quantity > bundle.quantity) {
          const response = await shop.carts.removeBundle(
            cartId,
            Number.parseInt(sdkBundle.externalId),
            inRemoteCart.quantity - bundle.quantity
          )

          checkoutContext.setRemoteCart(response)
        } else {
          itemsToAdd.current.push({
            itemID: Number.parseInt(sdkBundle.externalId),
            quantity: bundle.quantity - inRemoteCart.quantity,
            isBundle: true,
          })
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getBundle]
  )

  return syncCartItems
}

export const useSyncCart = (): (() => Promise<boolean>) => {
  const notificationContext = useContext(NotificationContext)
  const cartContext = useContext(CartContext)
  const syncCartItems = useSyncCartItems()
  const clearRemoteCart = useClearRemoteCart()
  const checkoutContext = useContext(CheckoutContext)

  //TODO get updated designs for error notifications
  const handleError = useCallback(() => {
    notificationContext.setNotification({
      type: "danger",
      title: "Request failed",
      message: `Oops! Please try again later.`,
    })

    return false
  }, [notificationContext])

  const clearRemovedCartItems = useCallback(
    async (
      cartId: string,
      localCart: StoredItem[],
      initialRemoteCart: Cart
    ) => {
      //CLEAR PRODUCTS
      const remoteProductsCart = initialRemoteCart.items
        .filter((item: CartItem) => {
          return !item.bundle
        })
        .map((item: CartItem) => {
          return {
            sku: item.product.sku,
            quantity: item.quantity,
            productId: item.product.id,
          }
        })

      let updatedRemoteCart: YocoCom.RemoteCart | undefined = undefined

      const localCartSkus = new Set(
        localCart.filter(isStoredProduct).map((item) => item.sku)
      )
      const differencesCartProducts = remoteProductsCart.filter(
        (product) => !localCartSkus.has(product.sku)
      )
      for (const product of differencesCartProducts) {
        if (product.sku) {
          updatedRemoteCart = await shop.carts.removeProduct(
            cartId,
            product.productId,
            product.quantity
          )
        }
      }

      //CLEAR BUNDLES
      const remoteBundleCart = initialRemoteCart.items.map((item) => {
        return {
          bundleCode: item.bundle?.bundle_code,
          quantity: item.quantity,
          id: item.bundle?.id.toString(),
        }
      })

      const reducedBundlesCart = remoteBundleCart.filter(
        (bundle, index) =>
          remoteBundleCart.findIndex(
            (other) => other.bundleCode === bundle.bundleCode
          ) === index
      )

      const localCartBundleCodes = new Set<string | undefined>(
        localCart.filter(isStoredBundle).map((item) => item.bundleCode)
      )
      const differencesCart = reducedBundlesCart.filter(
        (bundle) => !localCartBundleCodes.has(bundle.bundleCode)
      )
      for (const bundle of differencesCart) {
        if (bundle.id) {
          updatedRemoteCart = await shop.carts.removeBundle(
            cartId,
            Number.parseInt(bundle.id),
            bundle.quantity
          )
        }
      }
      if (updatedRemoteCart) {
        checkoutContext.setRemoteCart(updatedRemoteCart)
      }
    },
    [checkoutContext]
  )

  const syncCart = useCallback(async () => {
    const cartId = fetchCartId()
    if (!cartId) {
      console.error(`Failed to sync cart with invalid ID: ${cartId}`)
      clearCart()
      cartContext.resetCart()
      navigate(withPrefix("/"))
      return handleError()
    }

    try {
      const initialRemoteCart = await shop.carts.get(cartId)

      if (!checkoutContext.remoteCart) {
        checkoutContext.setRemoteCart(initialRemoteCart)
      }

      const localCart = fetchCartProducts()

      if (initialRemoteCart && initialRemoteCart.is_paid) {
        clearCart()
        cartContext.resetCart()
        navigate(withPrefix("/"))
        return true
      }

      if (!localCart) {
        console.error(`Unable to sync empty cart`)
        clearCart()
        cartContext.resetCart()
        navigate(withPrefix("/"))
        return handleError()
      }

      if (cartContext.id && localCart.length < 1) {
        await clearRemoteCart()
        return true
      }

      try {
        await syncCartItems(cartId, localCart, initialRemoteCart)
      } catch {
        return handleError()
      }

      await clearRemovedCartItems(cartId, localCart, initialRemoteCart)

      return true
    } catch (error: any) {
      if (error.response && error.response.status === 404) {
        clearCart()
        cartContext.resetCart()
        navigate(withPrefix("/"))
      }
      return handleError()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleError, syncCartItems, cartContext])

  return syncCart
}

export const useClearPaidCart = () => {
  const cartContext = useContext(CartContext)

  const checkPaidCart = useCallback(async () => {
    const cartId = fetchCartId()
    if (!cartId) {
      return
    }
    try {
      const initialRemoteCart = await shop.carts.get(cartId)

      if (initialRemoteCart && initialRemoteCart.is_paid) {
        clearCart()
        cartContext.resetCart()
      }
    } catch (error: any) {
      if (error.response && error.response.status === 404) {
        clearCart()
        cartContext.resetCart()
      }
    }
  }, [cartContext])

  return checkPaidCart
}

export const useValidatePromoCode = () => {
  const cartId = fetchCartId()

  const validatePromoCode = useCallback(
    async (promoCode: string) => {
      if (cartId) {
        const response = await shop.carts.validatePromoCode(cartId, promoCode)
        return response
      }
      return { valid: false, message: "Your cart is not set up correctly." }
    },
    [cartId]
  )

  return validatePromoCode
}

export const usePreApplyPromoCode = () => {
  const notificationContext = useContext(NotificationContext)
  const checkoutContext = useContext(CheckoutContext)
  const validatePromoCode = useValidatePromoCode()

  const updatePromoCode = useCallback(
    async (
      cartId: string,
      promoCode: string
    ): Promise<{
      cart?: Cart
      error?: unknown
    }> => {
      try {
        const responseData = await shop.carts.update(cartId, {
          promo_code_str: promoCode,
        })
        return { cart: responseData, error: undefined }
      } catch (error: any) {
        return {
          cart: undefined,
          error: error?.response?.data?.promo_code_str?.[0],
        }
      }
    },
    []
  )

  const preApplyPromoCode = useCallback(async () => {
    const cartPromoCode = fetchCartPromoCode()
    const cartId = fetchCartId()

    if (cartId && cartPromoCode) {
      const remoteCart = await shop.carts.get(cartId)
      const remoteCartPromoCode = remoteCart.promo_code?.code

      let updateResponse: {
        cart?: Cart
        error?: unknown
      } = {
        cart: undefined,
        error: undefined,
      }

      updateResponse = await updatePromoCode(cartId, cartPromoCode)
      if (updateResponse.cart) {
        checkoutContext.setRemoteCart(updateResponse.cart)
        notificationContext.setNotification({
          type: "success",
          title: `The promo code "${cartPromoCode}" has been applied to your cart`,
        })
      } else if (!updateResponse.cart && remoteCartPromoCode) {
        const initialError = updateResponse.error
        const { valid } = await validatePromoCode(remoteCartPromoCode)

        if (valid) {
          updateResponse.error = undefined
          storeCartPromoCode(remoteCartPromoCode)
          notificationContext.setNotification({
            type: "warning",
            title: `Promo code "${cartPromoCode}" reverted to "${remoteCartPromoCode}"`,
            message: `"${cartPromoCode}" is invalid so "${remoteCartPromoCode}" was re-applied.`,
          })
        } else {
          updateResponse.error = initialError
        }
      }

      if (updateResponse.error && typeof updateResponse.error === "string") {
        updatePromoCode(cartId, "").then((removedPromoResponse) => {
          if (removedPromoResponse.cart) {
            checkoutContext.setRemoteCart(removedPromoResponse.cart)
          }
        })
        clearCartPromoCode()
        notificationContext.setNotification({
          type: "danger",
          title: `Could not pre-set promo code "${cartPromoCode}"`,
          message: updateResponse.error.toLowerCase(),
        })
      }

      return updateResponse.cart ? true : false
    }

    return true
  }, [notificationContext, checkoutContext, updatePromoCode, validatePromoCode])

  return preApplyPromoCode
}

export const useApplyPromoCode = (): ((
  promoCode: string
) => Promise<boolean>) => {
  const initCart = useInitCart()
  const checkoutContext = useContext(CheckoutContext)
  const notificationContext = useContext(NotificationContext)

  const applyPromoCode = useCallback(
    async (promoCode: string) => {
      const cart = await initCart()

      if (!cart) return false

      try {
        const response = await shop.carts.update(cart.id, {
          promo_code_str: promoCode,
        })

        checkoutContext.setRemoteCart(response)

        if (response?.promo_code?.code) {
          notificationContext.setNotification({
            type: "success",
            title: "Promo code applied to order",
          })
        } else {
          notificationContext.setNotification({
            type: "success",
            title: "Promo code has been removed",
          })
        }
        return true
      } catch (error: any) {
        notificationContext.setNotification({
          type: "danger",
          title: "Failed to apply promotion code",
          message:
            (error?.promo_code_str?.[0] ?? error?.message)?.toLowerCase() ?? "",
        })
        return false
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [initCart]
  )

  return applyPromoCode
}

export const useGenerateOrderNumber = (): (() => Promise<Cart | null>) => {
  const notificationContext = useContext(NotificationContext)

  const generateOrderNumber = useCallback(async () => {
    const cartId = fetchCartId()

    if (!cartId) return null

    try {
      const response = await shop.carts.generateOrderNumber(cartId)
      return response
    } catch (error: any) {
      // TODO - better typing for all SDK errors
      notificationContext.setNotification({
        type: "danger",
        title: "Failed to generate order number",
        message: error?.message,
      })
      throw error
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return generateOrderNumber
}

export const useClearRemoteCart = () => {
  const { setRemoteCart } = useContext(CheckoutContext)
  const { setNotification } = useContext(NotificationContext)

  const clearRemoteCart = useCallback(async () => {
    try {
      const cartId = fetchCartId()
      if (!cartId) return

      const initialRemoteCart = await shop.carts.get(cartId)
      if (!initialRemoteCart) return

      if (initialRemoteCart.items.length > 0) {
        let clearedCart: Cart | undefined = undefined

        clearedCart = await shop.carts.clear(cartId)

        if (clearedCart) {
          setRemoteCart(clearedCart)
        }
        return
      }
      setRemoteCart(initialRemoteCart)
    } catch (error: any) {
      setNotification({
        type: "danger",
        title: "Failed to clear remote cart",
        message: error?.message,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return clearRemoteCart
}

export const useHandleSignupCheckout = () => {
  const syncCart = useSyncCart()
  const generateOrderNumber = useGenerateOrderNumber()
  const checkoutContext = useContext(CheckoutContext)
  const applyPromoCode = usePreApplyPromoCode()
  const [isLoading, setIsLoading] = useState(false)
  const [isSuccessful, setIsSuccessful] = useState(false)

  const handleRedirect = useCallback(
    async (businessUUID?: string) => {
      setIsLoading(true)

      try {
        if (!checkoutContext.remoteCart?.order_number) {
          await generateOrderNumber()
        }

        // TODO - fix the way useSyncCart and usePreApplyPromoCode bubbles up errors
        const syncCartResponse = await syncCart()

        if (!syncCartResponse) throw new Error("Failed to sync cart")

        const applyPromoCodeResponse = await applyPromoCode()

        if (!applyPromoCodeResponse)
          throw new Error("Failed to apply promo code")

        trackSegmentEvent("shop_checkout")
        setIsSuccessful(true)
        let URL: string

        if (process.env.GATSBY_OAUTH_REDIRECT === "true" && businessUUID) {
          URL = `${
            process.env.GATSBY_SHOP_URL
          }/business/${businessUUID}/${fetchCartId()}`

          const signupQueryString = getSignupQueryString()

          if (signupQueryString) {
            URL += `?${signupQueryString}`
          }
        } else {
          URL = `${
            process.env.GATSBY_SHOP_SIGNUP_URL
          }?cartUUID=${fetchCartId()}`

          const signupQueryString = getSignupQueryString()

          if (signupQueryString) {
            URL += `&${signupQueryString}`
          }

          if (!URL.includes("userJourney")) {
            URL += `&userJourney=yocoshop`
          }
        }

        if (checkoutContext.chosenServices.length > 0) {
          if (URL.includes("?")) {
            URL += "&"
          } else {
            URL += "?"
          }

          URL += `skipServiceSelectionStep=true&services=${checkoutContext.chosenServices.join(",")}`
        }

        globalThis.location.href = URL
        setIsLoading(false)
      } catch (error) {
        console.error(`Failed to handleRedirect....${error}`)
        setIsLoading(false)
      }
    },
    [checkoutContext, generateOrderNumber, syncCart, applyPromoCode]
  )

  return { isLoading, isSuccessful, handleRedirect }
}

export const useHandleLoginCheckout = () => {
  const checkoutContext = useContext(CheckoutContext)
  const cartContext = useContext(CartContext)
  const notificationContext = useContext(NotificationContext)
  const laxSession = process.env.GATSBY_LAX_SESSION === "true"

  const handleLoginCheckout = useCallback(async () => {
    trackSegmentEvent("website_oauth_checkout_initialised")
    checkoutContext.setIsHandlingLogin(true)

    try {
      if (sessionStorageAvailable()) {
        const session = await shop.oauth.startSession(laxSession)
        sessionStorage.setItem(CSRF_TOKEN_SESSION_KEY, session.csrf_token)

        const redirectUrl = await shop.oauth.getAuthUrl(
          session.csrf_token,
          laxSession
        )
        globalThis.location.href = redirectUrl
      }
      // We leave `isHandlingLogin` state `true` here since the page will redirect
    } catch (error) {
      console.error(`Failed to handleLoginCheckout....${error}`)
      navigate(withPrefix("/cart-overview/"))
      setTimeout(
        () =>
          notificationContext.setNotification({
            type: "danger",
            title: "Failed to login",
            message: `Oops! Please try again later.`,
          }),
        500
      )

      cartContext.setCartPreviewOpen(false)
      checkoutContext.setIsHandlingLogin(false)
    }
  }, [checkoutContext, laxSession, cartContext, notificationContext])

  // ToDo: we could return the `isHandlingLogin` state here but it
  // would just serve as a proxy to the state in the checkout context
  return { handleLoginCheckout }
}
